import type { RunSummary } from "./types";

// Searchable haystack for a run — a flat list of strings extracted from
// every place the user might want to search. Computed once at bundle load
// and cached in a side-table keyed by runId so per-keystroke filtering is
// O(N strings). We don't tokenize or stem; case-insensitive substring is
// enough. The cache lives at module scope keyed by string runId — call
// clearSearchIndex() before loading a new bundle so memory doesn't grow
// unbounded across repeated loads in the same session.
export type SearchHaystack = {
  runIdStrings: string[];          // runId, shortRunId
  packageStrings: string[];        // "npm:left-pad", "left-pad", "npm"
  versionStrings: string[];        // every affected/cached/unblocked version
  sourceStrings: string[];         // sources like "ghsa-malware", "osv"
  urlStrings: string[];            // every sourceItemUrl + classifier/extraction sourceUrl
  advisoryStrings: string[];       // GHSA-* / MAL-* IDs + case IDs
  titleStrings: string[];          // classifier sourceTitles
  reasonStrings: string[];         // decision.reason
};

const haystackByRunId = new Map<string, SearchHaystack>();

// Drop every cached haystack. Call before loading a new bundle so haystacks
// from the previous bundle's runs don't linger in memory.
export function clearSearchIndex(): void {
  haystackByRunId.clear();
}

export function buildSearchIndex(run: RunSummary): void {
  const runIdStrings = [run.runId, run.shortRunId].filter(Boolean);
  const packageStrings: string[] = [];
  const versionStrings: string[] = [];
  const sourceStrings: string[] = [];
  const urlStrings: string[] = [];
  const advisoryStrings: string[] = [];
  const titleStrings: string[] = [];
  const reasonStrings: string[] = [];

  for (const d of run.decisions) {
    if (d.ecosystem) packageStrings.push(d.ecosystem);
    if (d.name) packageStrings.push(d.name);
    if (d.ecosystem && d.name) packageStrings.push(`${d.ecosystem}:${d.name}`);
    for (const v of d.affectedVersions) versionStrings.push(v);
    for (const v of d.cachedVersions) versionStrings.push(v);
    for (const s of d.sources) sourceStrings.push(s);
    if (d.sourceItemUrl) urlStrings.push(d.sourceItemUrl);
    for (const a of d.sourceAdvisoryIds) advisoryStrings.push(a);
    if (d.reason) reasonStrings.push(d.reason);
  }
  for (const c of run.classifications) {
    const url = c.data?.sourceUrl as string | undefined;
    const title = c.data?.sourceTitle as string | undefined;
    if (url) urlStrings.push(url);
    if (title) titleStrings.push(title);
  }
  for (const e of run.extractions) {
    const url = e.data?.sourceUrl as string | undefined;
    if (url) urlStrings.push(url);
  }
  for (const g of run.githubLookups) {
    const eco = g.data?.packageEcosystem as string | undefined;
    const name = g.data?.packageName as string | undefined;
    if (eco) packageStrings.push(eco);
    if (name) packageStrings.push(name);
    if (eco && name) packageStrings.push(`${eco}:${name}`);
  }
  for (const u of run.unblocks) {
    if (u.package?.name) packageStrings.push(u.package.name);
    if (u.package?.ecosystem) packageStrings.push(u.package.ecosystem);
    for (const v of u.versions || []) versionStrings.push(v);
    if (u.caseId) advisoryStrings.push(u.caseId);
  }

  haystackByRunId.set(run.runId, {
    runIdStrings: dedup(runIdStrings),
    packageStrings: dedup(packageStrings),
    versionStrings: dedup(versionStrings),
    sourceStrings: dedup(sourceStrings),
    urlStrings: dedup(urlStrings),
    advisoryStrings: dedup(advisoryStrings),
    titleStrings: dedup(titleStrings),
    reasonStrings: dedup(reasonStrings),
  });
}

function dedup(arr: string[]): string[] {
  return [...new Set(arr.filter(Boolean))];
}

// Result of testing a run against a search query.
//   matched: true if any haystack string contained the query.
//   count:   total haystack strings that matched (a rough "hit count" the
//            row badge displays).
//   fields:  which field categories had hits (for hint text).
export type SearchResult = {
  matched: boolean;
  count: number;
  fields: Set<keyof SearchHaystack>;
};

export function matchesQuery(run: RunSummary, query: string): SearchResult {
  const q = query.trim().toLowerCase();
  if (!q) return { matched: true, count: 0, fields: new Set() };
  let h = haystackByRunId.get(run.runId);
  if (!h) {
    buildSearchIndex(run);
    h = haystackByRunId.get(run.runId)!;
  }
  let count = 0;
  const fields = new Set<keyof SearchHaystack>();
  for (const key of Object.keys(h) as (keyof SearchHaystack)[]) {
    for (const s of h[key]) {
      if (s.toLowerCase().includes(q)) {
        count += 1;
        fields.add(key);
      }
    }
  }
  return { matched: count > 0, count, fields };
}

// Field-category labels for the UI ("matched: package, blog post").
export const FIELD_LABEL: Record<keyof SearchHaystack, string> = {
  runIdStrings: "run id",
  packageStrings: "package",
  versionStrings: "version",
  sourceStrings: "source",
  urlStrings: "url",
  advisoryStrings: "advisory id",
  titleStrings: "title",
  reasonStrings: "reason",
};
